import { useEffect, useState, useRef } from "react";
import { AppLayout } from "@/components/AppLayout";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ShieldCheck, ShieldAlert, Camera } from "lucide-react";

export function Profile() {
  const { t, tl } = useI18n();
  const { user, refreshUser } = useAuth();
  const [p, setP] = useState({ first_name: "", last_name: "", phone: "", dob: "", emergency_contact: "", goals: "", experience: "", id_type: "cedula", id_number: "" });
  const [h, setH] = useState({ injuries: "", pain: "", surgeries: "", pregnancy: "", cardiovascular: "", medications: "", restrictions: "", emergency_contact: "", consent: false });
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const avatarUrl = user?.avatar_updated_at
    ? `${process.env.REACT_APP_BACKEND_URL}/api/avatars/${user.user_id}?v=${encodeURIComponent(user.avatar_updated_at)}`
    : null;

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post("/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshUser();
      toast.success(t("common.success"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  useEffect(() => {
    if (user) setP({
      first_name: user.first_name || "", last_name: user.last_name || "", phone: user.phone || "",
      dob: user.dob || "", emergency_contact: user.emergency_contact || "", goals: user.goals || "", experience: user.experience || "",
      id_type: user.id_type || "cedula", id_number: user.id_number || "",
    });
  }, [user]);

  const saveProfile = async () => {
    try { await api.put("/me/profile", p); await refreshUser(); toast.success(t("common.success")); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveHealth = async () => {
    if (!h.consent) { toast.error(t("health.consent")); return; }
    try {
      const { data } = await api.post("/me/health", h);
      await refreshUser();
      if (data.health_flag === "attention") toast.warning(t("health.warn"));
      else toast.success(t("common.success"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const HF = (k, label, textarea) => (
    <div>
      <Label>{label}</Label>
      {textarea
        ? <Textarea data-testid={`health-${k}`} value={h[k]} onChange={(e) => setH({ ...h, [k]: e.target.value })} className="mt-1.5" />
        : <Input data-testid={`health-${k}`} value={h[k]} onChange={(e) => setH({ ...h, [k]: e.target.value })} className="mt-1.5 h-12" />}
    </div>
  );

  return (
    <AppLayout>
      <div className="mb-6 flex items-center gap-3">
        <h1 className="font-display text-3xl font-bold tracking-tight">{t("profile.title")}</h1>
        {user?.health_completed
          ? <span className="flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"><ShieldCheck className="h-4 w-4" />{t("profile.healthDone")}</span>
          : <span className="flex items-center gap-1 rounded-full bg-destructive/10 px-3 py-1 text-xs font-medium text-destructive"><ShieldAlert className="h-4 w-4" />{t("profile.healthCta")}</span>}
      </div>

      <Tabs defaultValue="personal" className="max-w-2xl">
        <TabsList>
          <TabsTrigger value="personal" data-testid="tab-personal">{t("profile.personal")}</TabsTrigger>
          <TabsTrigger value="health" data-testid="tab-health">{t("profile.health")}</TabsTrigger>
        </TabsList>

        <TabsContent value="personal" className="mt-5 space-y-4 rounded-2xl border border-border bg-card p-6">
          <div className="flex items-center gap-4" data-testid="avatar-section">
            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border-2 border-primary/30 bg-secondary">
              {avatarUrl
                ? <img src={avatarUrl} alt="avatar" className="h-full w-full object-cover" data-testid="avatar-img" />
                : <span className="font-display text-2xl font-bold text-muted-foreground">{(p.first_name?.[0] || user?.name?.[0] || "?").toUpperCase()}</span>}
            </div>
            <div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadAvatar} className="hidden" data-testid="avatar-input" />
              <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="avatar-upload-btn" className="rounded-full">
                <Camera className="mr-1.5 h-4 w-4" />{uploading ? tl({ es: "Subiendo...", en: "Uploading...", fr: "Envoi..." }) : tl({ es: "Cambiar foto", en: "Change photo", fr: "Changer la photo" })}
              </Button>
              <p className="mt-1 text-xs text-muted-foreground">JPG · PNG · WEBP — max 5MB</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("auth.firstName")}</Label><Input data-testid="profile-firstname" value={p.first_name} onChange={(e) => setP({ ...p, first_name: e.target.value })} className="mt-1.5 h-12" /></div>
            <div><Label>{t("auth.lastName")}</Label><Input data-testid="profile-lastname" value={p.last_name} onChange={(e) => setP({ ...p, last_name: e.target.value })} className="mt-1.5 h-12" /></div>
          </div>
          <div><Label>{t("auth.phone")}</Label><Input data-testid="profile-phone" value={p.phone} onChange={(e) => setP({ ...p, phone: e.target.value })} className="mt-1.5 h-12" /></div>
          <div>
            <Label>{tl({ es: "Documento (facturación)", en: "Document (billing)", fr: "Document (facturation)" })}</Label>
            <div className="mt-1.5 grid grid-cols-3 gap-3">
              <select data-testid="profile-id-type" value={p.id_type} onChange={(e) => setP({ ...p, id_type: e.target.value })}
                className="col-span-1 h-12 rounded-lg border border-border bg-card px-3 text-sm">
                <option value="cedula">Cédula</option>
                <option value="ruc">RUC</option>
                <option value="pasaporte">{tl({ es: "Pasaporte", en: "Passport", fr: "Passeport" })}</option>
              </select>
              <Input data-testid="profile-id-number" placeholder={tl({ es: "N° de documento", en: "Document no.", fr: "N° de document" })}
                value={p.id_number} onChange={(e) => setP({ ...p, id_number: e.target.value })} className="col-span-2 h-12" />
            </div>
          </div>
          <div><Label>{t("profile.dob")}</Label><Input data-testid="profile-dob" type="date" value={p.dob || ""} onChange={(e) => setP({ ...p, dob: e.target.value })} className="mt-1.5 h-12" /></div>
          <div><Label>{t("profile.emergency")}</Label><Input data-testid="profile-emergency" value={p.emergency_contact} onChange={(e) => setP({ ...p, emergency_contact: e.target.value })} className="mt-1.5 h-12" /></div>
          <div><Label>{t("profile.goals")}</Label><Textarea data-testid="profile-goals" value={p.goals} onChange={(e) => setP({ ...p, goals: e.target.value })} className="mt-1.5" /></div>
          <div><Label>{t("profile.experience")}</Label><Textarea data-testid="profile-experience" value={p.experience} onChange={(e) => setP({ ...p, experience: e.target.value })} className="mt-1.5" /></div>
          <Button data-testid="save-profile-btn" onClick={saveProfile} className="rounded-full">{t("profile.save")}</Button>
        </TabsContent>

        <TabsContent value="health" className="mt-5 space-y-4 rounded-2xl border border-border bg-card p-6">
          {HF("injuries", t("health.injuries"), true)}
          {HF("pain", t("health.pain"))}
          {HF("surgeries", t("health.surgeries"))}
          {HF("pregnancy", t("health.pregnancy"))}
          {HF("cardiovascular", t("health.cardiovascular"))}
          {HF("medications", t("health.medications"))}
          {HF("restrictions", t("health.restrictions"))}
          {HF("emergency_contact", t("health.emergency"))}
          <label className="flex items-start gap-2 text-sm"><input data-testid="health-consent" type="checkbox" checked={h.consent} onChange={(e) => setH({ ...h, consent: e.target.checked })} className="mt-1" />{t("health.consent")}</label>
          <Button data-testid="save-health-btn" onClick={saveHealth} className="rounded-full">{t("health.submit")}</Button>
        </TabsContent>
      </Tabs>
    </AppLayout>
  );
}
